import React, { createContext, useContext, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "./components/Login";
import Shell from "./components/Shell";
import GatewayChat from "./components/GatewayChat";
import PolicyBuilder from "./components/PolicyBuilder";
import AuditLog from "./components/AuditLog";
import Dashboard from "./components/Dashboard";
import Report from "./components/Report";
import Onboarding from "./components/Onboarding";
import Users from "./components/Users";
import SignOff from "./components/SignOff";
import Integration from "./components/Integration";
import EmployeeDashboard from "./components/EmployeeDashboard";
import PlatformLogin from "./components/PlatformLogin";
import PlatformOverview from "./components/PlatformOverview";
import Landing from "./components/Landing";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [session, setSession] = useState(() => {
    const raw = localStorage.getItem("lucidgate_session");
    return raw ? JSON.parse(raw) : null;
  });
  const [platform, setPlatform] = useState(() => {
    const raw = localStorage.getItem("lucidgate_platform");
    return raw ? JSON.parse(raw) : null;
  });

  const login = (data) => {
    localStorage.setItem("lucidgate_session", JSON.stringify(data));
    setSession(data);
  };
  const logout = () => {
    localStorage.removeItem("lucidgate_session");
    setSession(null);
  };
  const refreshUser = (user) => {
    const next = { ...session, user };
    localStorage.setItem("lucidgate_session", JSON.stringify(next));
    setSession(next);
  };
  const platformLogin = (data) => {
    localStorage.setItem("lucidgate_platform", JSON.stringify(data));
    setPlatform(data);
  };
  const platformLogout = () => {
    localStorage.removeItem("lucidgate_platform");
    setPlatform(null);
  };

  return (
    <AuthCtx.Provider value={{ session, login, logout, refreshUser, platform, platformLogin, platformLogout }}>
      {children}
    </AuthCtx.Provider>
  );
}

function Protected({ children, adminOnly }) {
  const { session } = useAuth();
  if (!session) return <Navigate to="/login" replace />;
  if (adminOnly && session.user.role !== "admin")
    return <Navigate to="/my" replace />;
  return children;
}

function PlatformProtected({ children }) {
  const { platform } = useAuth();
  if (!platform) return <Navigate to="/platform/login" replace />;
  return children;
}

function GateProtected({ children }) {
  if (sessionStorage.getItem("lucidgate_gate_ok") !== "1")
    return <Navigate to="/welcome" replace />;
  return children;
}

function RoleIndex() {
  const { session } = useAuth();
  return <Navigate to={session.user.role === "admin" ? "chat" : "my"} replace />;
}

function AppRoutes() {
  const { session, platform } = useAuth();
  return (
    <Routes>
      <Route path="/welcome" element={<Landing />} />
      <Route path="/login" element={<GateProtected>{session ? <Navigate to="/" replace /> : <Login />}</GateProtected>} />
      <Route path="/platform/login" element={<GateProtected>{platform ? <Navigate to="/platform" replace /> : <PlatformLogin />}</GateProtected>} />
      <Route path="/platform" element={<GateProtected><PlatformProtected><PlatformOverview /></PlatformProtected></GateProtected>} />
      <Route
        path="/"
        element={
          <GateProtected>
            <Protected>
              <Shell />
            </Protected>
          </GateProtected>
        }
      >
        <Route index element={<RoleIndex />} />
        <Route path="chat" element={<GatewayChat />} />
        <Route path="my" element={<EmployeeDashboard />} />
        <Route path="signoff" element={<SignOff />} />
        <Route path="policy" element={<Protected adminOnly><PolicyBuilder /></Protected>} />
        <Route path="audit" element={<Protected adminOnly><AuditLog /></Protected>} />
        <Route path="dashboard" element={<Protected adminOnly><Dashboard /></Protected>} />
        <Route path="report" element={<Protected adminOnly><Report /></Protected>} />
        <Route path="onboarding" element={<Protected adminOnly><Onboarding /></Protected>} />
        <Route path="users" element={<Protected adminOnly><Users /></Protected>} />
        <Route path="integration" element={<Protected adminOnly><Integration /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/welcome" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
